import type {
  AnalyticsPageContext,
  AnalyticsProvider,
  AnalyticsTrackEvent,
} from '../types';
import {PAYMENT_GOAL_NAMES} from '../types';

declare global {
  interface Window {
    ym?: (...args: any[]) => void;
    dataLayer?: unknown[];
  }
}

export class YandexMetrikaProvider implements AnalyticsProvider {
  constructor(private readonly counterId: number | null) {}

  page(ctx: AnalyticsPageContext): void {
    if (!this.counterId || typeof window.ym !== 'function') return;

    window.ym(this.counterId, 'hit', ctx.pageLocation, {
      referer: ctx.referrer ?? document.referrer,
      title: ctx.title ?? document.title,
    });
  }

  track(event: AnalyticsTrackEvent): void {
    if (!this.counterId || typeof window.ym !== 'function') return;

    const params: Record<string, unknown> = {...(event.props ?? {})};
    if (event.revenue) {
      params.order_price = event.revenue.value;
      params.currency = event.revenue.currency;
    }

    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null),
    );

    if (Object.keys(cleanParams).length > 0) {
      window.ym(this.counterId, 'reachGoal', event.name, cleanParams);
    } else {
      window.ym(this.counterId, 'reachGoal', event.name);
    }

    this.pushEcommerce(event);
  }

  private pushEcommerce(event: AnalyticsTrackEvent): void {
    if (
      !event.revenue ||
      !(PAYMENT_GOAL_NAMES as readonly string[]).includes(event.name)
    ) {
      return;
    }

    const orderId = event.props?.order_id;
    const products = (event.products ?? []).length
      ? event.products!
      : [
          {
            id: event.name,
            name: event.name,
            price: event.revenue.value,
            quantity: 1,
            category: event.name,
          },
        ];

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      ecommerce: {
        currencyCode: event.revenue.currency,
        purchase: {
          actionField: {
            id: orderId != null ? String(orderId) : event.name,
          },
          products: products.map(product => ({
            id: product.id,
            name: product.name,
            price: product.price,
            quantity: product.quantity ?? 1,
            ...(product.category ? {category: product.category} : {}),
            ...(product.variant ? {variant: product.variant} : {}),
          })),
        },
      },
    });
  }
}
